import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft } from 'lucide-react'
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { ApiError } from '@/api/client'
import { QueryState } from '@/components/QueryState'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card, CardHeader } from '@/components/ui/Card'
import { Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import {
  SOURCE_LABELS,
  formatMoney,
  formatSide,
  getEntry,
  reverseEntry,
  type JournalEntry,
} from '@/features/accounting/api'
import { useAuth } from '@/features/auth/store'
import { StatusPill } from '@/routes/JournalEntries'
import { toast } from '@/lib/toast'

/**
 * Ask for a reason, then post the mirror image.
 *
 * The reason is required by the API and not by accident: a reversal with no explanation is
 * the pair of entries an auditor asks about six months later, when nobody remembers. The
 * date is optional and only matters when the original month is closed — leaving it blank
 * lets the server pick the first month that will accept it.
 */
function ReverseDialog({
  entry,
  open,
  onClose,
}: {
  entry: JournalEntry
  open: boolean
  onClose: () => void
}) {
  const [reason, setReason] = useState('')
  const [onDate, setOnDate] = useState('')
  const [error, setError] = useState<string | null>(null)
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const reverse = useMutation({
    mutationFn: () => reverseEntry(entry.id, reason.trim(), onDate || undefined),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ['journal-entries'] })
      void queryClient.invalidateQueries({ queryKey: ['journal-entry', entry.id] })
      void queryClient.invalidateQueries({ queryKey: ['trial-balance'] })
      toast.success(`Posted ${created.entry_number}.`)
      onClose()
      navigate(`/accounting/journal/${created.id}`)
    },
    onError: (mutationError: unknown) => {
      setError(
        mutationError instanceof ApiError
          ? mutationError.message
          : 'Could not reach the server. Nothing was posted.',
      )
    },
  })

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Reverse ${entry.entry_number}`}
      description="A new entry with every leg the other way round. The original stays exactly as it is."
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={reverse.isPending}>
            Cancel
          </Button>
          <Button
            loading={reverse.isPending}
            disabled={reason.trim().length < 3}
            onClick={() => {
              setError(null)
              reverse.mutate()
            }}
          >
            Post reversal
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <Alert tone="error">{error}</Alert>}

        <Field label="Reason" hint="Kept on both entries. At least three characters.">
          {(props) => (
            <Input
              {...props}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Invoice raised against the wrong customer"
            />
          )}
        </Field>

        <Field
          label="Post it on"
          hint="Leave blank to use the original date, or today if that month is closed."
        >
          {(props) => (
            <Input
              {...props}
              type="date"
              value={onDate}
              onChange={(event) => setOnDate(event.target.value)}
            />
          )}
        </Field>
      </div>
    </Modal>
  )
}

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </dt>
      <dd className="mt-0.5 text-sm">{children}</dd>
    </div>
  )
}

export function JournalEntryDetail() {
  const { entryId = '' } = useParams()
  const [reversing, setReversing] = useState(false)
  const can = useAuth((s) => s.can)

  const {
    data: entry,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['journal-entry', entryId],
    queryFn: () => getEntry(entryId),
    retry: false,
  })

  // Only a live entry can be reversed, and only once: a reversal of a reversal is a fresh
  // posting, not a correction, and the API refuses the second attempt anyway.
  const canReverse = entry?.status === 'posted' && can('journal_entries', 'update')

  return (
    <div className="space-y-4">
      <Link
        to="/accounting/journal"
        className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Journal
      </Link>

      <QueryState isLoading={isLoading} error={error}>
        {entry && (
          <div className="space-y-4">
            <header className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="font-mono text-xl font-semibold tracking-tight">
                    {entry.entry_number}
                  </h1>
                  <StatusPill status={entry.status} />
                </div>
                <p className="text-sm text-slate-500 dark:text-slate-400">{entry.description}</p>
              </div>
              {canReverse && (
                <Button variant="secondary" onClick={() => setReversing(true)}>
                  Reverse entry
                </Button>
              )}
            </header>

            {entry.status === 'reversed' && (
              <Alert tone="info" title="This entry has been reversed">
                Its effect on the books has been cancelled by a later entry. Both are kept — the
                ledger records what happened, including the mistake.
              </Alert>
            )}

            {entry.reversal_of_id && (
              <Alert tone="info" title="This is a reversal">
                It cancels{' '}
                <Link
                  to={`/accounting/journal/${entry.reversal_of_id}`}
                  className="font-medium underline"
                >
                  the entry it was raised against
                </Link>
                .
              </Alert>
            )}

            <Card>
              <CardHeader title="Entry" />
              <dl className="grid grid-cols-2 gap-4 p-5 tablet:grid-cols-4">
                <Detail label="Date">
                  {new Date(`${entry.entry_date}T00:00:00`).toLocaleDateString()}
                </Detail>
                <Detail label="Raised by">
                  {entry.source_type === 'invoice' && entry.source_id ? (
                    <Link to={`/invoices/${entry.source_id}`} className="hover:underline">
                      Invoice
                    </Link>
                  ) : (
                    (SOURCE_LABELS[entry.source_type] ?? entry.source_type)
                  )}
                </Detail>
                <Detail label="Total">
                  <span className="tabular-nums">{formatMoney(entry.total_debit)}</span>
                </Detail>
                <Detail label="Posted">{new Date(entry.created_at).toLocaleString()}</Detail>
                {entry.memo && (
                  <div className="col-span-2 tablet:col-span-4">
                    <Detail label="Memo">{entry.memo}</Detail>
                  </div>
                )}
              </dl>
            </Card>

            <Card>
              <CardHeader
                title="Lines"
                description="Append-only. The database refuses an update or a delete on any of these rows."
              />
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <caption className="sr-only">Lines of {entry.entry_number}</caption>
                  <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-400">
                    <tr>
                      <th className="px-5 py-2 font-medium">Account</th>
                      <th className="px-5 py-2 font-medium">Description</th>
                      <th className="px-5 py-2 text-right font-medium">Debit</th>
                      <th className="px-5 py-2 text-right font-medium">Credit</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {entry.lines.map((line) => (
                      <tr key={line.id}>
                        <td className="px-5 py-2">
                          <Link
                            to={`/accounting/ledger/${line.account_id}`}
                            className="hover:underline"
                          >
                            <span className="font-mono text-xs">{line.account_code}</span>{' '}
                            {line.account_name}
                          </Link>
                        </td>
                        <td className="px-5 py-2 text-slate-500 dark:text-slate-400">
                          {line.description}
                        </td>
                        <td className="px-5 py-2 text-right tabular-nums">
                          {formatSide(line.debit)}
                        </td>
                        <td className="px-5 py-2 text-right tabular-nums">
                          {formatSide(line.credit)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="border-t border-slate-200 font-medium dark:border-slate-800">
                    <tr>
                      <td className="px-5 py-2" colSpan={2}>
                        Total
                      </td>
                      <td className="px-5 py-2 text-right tabular-nums">
                        {formatMoney(entry.total_debit)}
                      </td>
                      <td className="px-5 py-2 text-right tabular-nums">
                        {formatMoney(entry.total_credit)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </Card>

            <ReverseDialog entry={entry} open={reversing} onClose={() => setReversing(false)} />
          </div>
        )}
      </QueryState>
    </div>
  )
}
